import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import { Toast } from '../../services/Toast'

const apiUrl = process.env.REACT_APP_API_URL

const BrandScreen = () => {
  const { userInfo } = useSelector((state) => state.userLogin) || {}
  const headers = { Authorization: `Bearer ${userInfo?.token}` }

  const [brands, setBrands] = useState([])
  const [loading, setLoading] = useState(true)
  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editSlug, setEditSlug] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [productCounts, setProductCounts] = useState({})

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/brands/all`)
      setBrands(res.data || [])

      // Fetch product counts per brand
      const prodRes = await axios.get(`${apiUrl}/api/products`)
      const counts = {}
      ;(prodRes.data?.products || []).forEach((p) => {
        const bid = p.brandId
        if (bid) counts[bid] = (counts[bid] || 0) + 1
      })
      setProductCounts(counts)
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  useEffect(() => { fetchBrands() }, [])

  const handleAdd = async () => {
    if (!newName.trim()) return
    const slug = newSlug.trim() || newName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')
    try {
      await axios.post(`${apiUrl}/api/brands/create`, { name: newName.trim(), slug }, { headers })
      setNewName('')
      setNewSlug('')
      fetchBrands()
      Toast({ type: 'success', message: 'Brand created' })
    } catch (err) {
      Toast({ type: 'error', message: err.response?.data?.message || 'Failed' })
    }
  }

  const handleUpdate = async (id) => {
    if (!editName.trim()) return
    try {
      await axios.put(`${apiUrl}/api/brands/update/${id}`, {
        name: editName.trim(),
        slug: editSlug.trim(),
        description: editDesc.trim(),
      }, { headers })
      setEditingId(null)
      fetchBrands()
      Toast({ type: 'success', message: 'Brand updated' })
    } catch (err) {
      Toast({ type: 'error', message: 'Failed to update' })
    }
  }

  const handleDelete = async (id, name) => {
    const count = productCounts[id] || 0
    const msg = count > 0
      ? `"${name}" is used by ${count} product(s). Delete anyway?`
      : `Delete "${name}"?`
    if (!window.confirm(msg)) return
    try {
      await axios.delete(`${apiUrl}/api/brands/delete/${id}`, { headers })
      fetchBrands()
      Toast({ type: 'success', message: 'Deleted' })
    } catch (err) {
      Toast({ type: 'error', message: 'Failed to delete' })
    }
  }

  const startEdit = (brand) => {
    setEditingId(brand._id)
    setEditName(brand.name || '')
    setEditSlug(brand.slug || '')
    setEditDesc(brand.description || '')
  }

  return (
    <div className="container mt-4">
      <h4 className="mb-4">Brands</h4>

      <div className="card mb-4">
        <div className="card-header"><strong>Add New Brand</strong></div>
        <div className="card-body">
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="Brand name"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value)
                setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              style={{ maxWidth: 250 }}
            />
            <input
              type="text"
              className="form-control"
              placeholder="Slug (auto-generated)"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              style={{ maxWidth: 250 }}
            />
            <button className="btn btn-success" onClick={handleAdd}>Add Brand</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>All Brands ({brands.length})</strong>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <p className="p-3 text-muted">Loading...</p>
          ) : (
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Description</th>
                  <th style={{ width: 80 }}>Products</th>
                  <th style={{ width: 180 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((b, i) => (
                  <tr key={b._id}>
                    <td>{i + 1}</td>
                    {editingId === b._id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editSlug}
                            onChange={(e) => setEditSlug(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editDesc}
                            onChange={(e) => setEditDesc(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdate(b._id)}
                          />
                        </td>
                        <td className="text-center">{productCounts[b._id] || 0}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <button className="btn btn-sm btn-success" onClick={() => handleUpdate(b._id)}>Save</button>
                            <button className="btn btn-sm btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td><strong>{b.name}</strong></td>
                        <td><span className="text-muted">{b.slug}</span></td>
                        <td><span className="text-muted">{b.description || '-'}</span></td>
                        <td className="text-center">
                          <span className={`badge ${productCounts[b._id] ? 'bg-primary' : 'bg-secondary'}`}>
                            {productCounts[b._id] || 0}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => startEdit(b)}>Edit</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(b._id, b.name)}>Delete</button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default BrandScreen
